import {FormControl, Label, TextInput} from '@primer/react'
import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import type {RegisteredRuleSchemaComponent} from '@github-ui/repos-rules-types/rules-types'
import type {RefObject} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import styles from './FloatField.module.css'

export function FloatField({field, value, onValueChange, readOnly, fieldRef, errors}: RegisteredRuleSchemaComponent) {
  const enabled = isFeatureEnabled('lifecycle_label_name_updates')

  if (field.type !== 'float') {
    throw new Error('Field type must be float')
  }

  if (readOnly) {
    return (
      <div>
        <span className="text-bold">{field.display_name}: </span>
        <span>{value as number}</span>
        <span className="d-block text-small color-fg-muted">{field.description}</span>
      </div>
    )
  }

  return (
    <FormControl>
      <FormControl.Label>
        {field.display_name}
        {field.beta &&
          (enabled ? (
            <BetaLabel className="ml-2" />
          ) : (
            <Label variant="success" className={styles.BetaLabel}>
              Beta
            </Label>
          ))}
      </FormControl.Label>
      {field.description !== '' ? <FormControl.Caption>{field.description}</FormControl.Caption> : null}
      <TextInput
        className="width-full"
        type={'number'}
        ref={fieldRef as RefObject<HTMLInputElement | null>}
        min={rangeBoundsFloat(field.allowed_range)?.min}
        max={rangeBoundsFloat(field.allowed_range)?.max}
        step="any"
        aria-invalid={errors.length > 0}
        value={(value || 0) as number}
        onChange={e => {
          onValueChange(parseFloat(e.target.value))
        }}
      />
      {errors.length > 0 && <FormControl.Validation variant="error">{errors[0]?.message}</FormControl.Validation>}
    </FormControl>
  )
}

function rangeBoundsFloat(range: string | undefined): {min: number | undefined; max: number | undefined} | undefined {
  if (typeof range === 'undefined') {
    return undefined
  }

  let [min, max] = range.split('..').map(num => parseFloat(num))

  if (min === undefined || isNaN(min)) {
    min = undefined
  }

  if (max === undefined || isNaN(max)) {
    max = undefined
  }

  return {min, max}
}
